/**
 * `/site/social` — **Social profiles & map** (FR-A8 §4; the owner, 2026-09-25,
 * after Wave 12 merged). Its own page under *Your website* since Wave 14 (C13,
 * ADR-0032 §1): it was a sixth section of *Settings → Your firm*, and
 * `/settings/firm#social` now redirects here. Wave 14E (T-4.66) drew it in the
 * builder's cards — the profiles and the map as two — with one Save on the
 * edit bar and *See it in your draft*, since the footer is where these show.
 *
 * The website footer's `social` and `map` blocks could be switched on in
 * Navigation, but **no staff screen edited what they draw**. `PATCH
 * /api/v1/site` has always taken `social` and `contact.map_url`, so nothing
 * was missing on the service: only the place to type them.
 *
 * **It is one fact in one place.** The site row stays the store. There is no
 * firm-profile column for any of this, and this section reads and writes the
 * site through the same client the builder uses. Two rules follow from that:
 *
 * - **`social` is merged, never replaced.** The five networks named here are
 *   what the section offers, and a key it does not know (`x-twitter` from an
 *   API client, say) is kept exactly as it was. The service drops an empty
 *   value, so clearing a field removes that network.
 * - **`contact` is merged the same way.** `map_url` is the only key this
 *   section owns. Every other contact key goes back as it was read, because the
 *   service's PATCH replaces the whole object.
 *
 * GET /site creates the site row if there is none (`site_or_start`), so there
 * is no *"no website yet"* state for anyone who may read it. The page says why
 * it has no fields when that read is refused or fails. Save is offered only to
 * somebody who may `site.can_update`, which is what the PATCH requires.
 */

import { Alert, Badge, Input, Skeleton } from '@fiveox/ui'
import { ExternalLink, MapPin, Share2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { EditBar, EditPage, useDraftPreviewHref, useLeaveGuard } from '../../components/edit'
import { SITE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { site as siteApi, type Site } from '../../lib/api/site'
import { SiteSection } from './SiteSection'

/** The networks this section offers, in the order it shows them. The key is
 *  what the footer title-cases into the link's text (`site/render.tsx`). */
export const SOCIAL_NETWORKS: ReadonlyArray<{ key: string; label: string; placeholder: string }> = [
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://www.linkedin.com/company/…' },
  { key: 'facebook', label: 'Facebook', placeholder: 'https://www.facebook.com/…' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://www.instagram.com/…' },
  { key: 'x', label: 'X', placeholder: 'https://x.com/…' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://www.youtube.com/@…' },
]

export interface SocialDraft {
  social: Record<string, string>
  map_url: string
}

/** A full http(s) address, or the reason it is not one. Blank is fine. The
 *  service takes a few more shapes (site-relative paths, `mailto:`, `tel:`),
 *  none of which is a profile or a map, so this is the stricter of the two. */
export function httpUrlProblem(value: string): string | null {
  const url = value.trim()
  if (!url) return null
  if (url.length > 500) return 'This link is longer than 500 characters.'
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return 'Write the full address, including https://.'
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return 'Use a web address that starts with https://.'
  }
  return null
}

export function draftFrom(site: Pick<Site, 'social' | 'contact'>): SocialDraft {
  const social: Record<string, string> = {}
  for (const { key } of SOCIAL_NETWORKS) social[key] = site.social?.[key] ?? ''
  return { social, map_url: site.contact?.map_url ?? '' }
}

/**
 * The PATCH body for a draft: only the halves that changed, each merged with
 * what the site already holds. `null` when nothing changed.
 */
export function socialPatch(
  site: Pick<Site, 'social' | 'contact'>,
  draft: SocialDraft,
): { social?: Record<string, string>; contact?: Record<string, string> } | null {
  const before = draftFrom(site)
  const body: { social?: Record<string, string>; contact?: Record<string, string> } = {}

  if (SOCIAL_NETWORKS.some(({ key }) => (draft.social[key] ?? '').trim() !== before.social[key])) {
    const social: Record<string, string> = {}
    const known = new Set(SOCIAL_NETWORKS.map((n) => n.key))
    for (const [key, url] of Object.entries(site.social ?? {})) {
      if (!known.has(key) && url) social[key] = url
    }
    for (const { key } of SOCIAL_NETWORKS) {
      const url = (draft.social[key] ?? '').trim()
      if (url) social[key] = url
    }
    body.social = social
  }

  if (draft.map_url.trim() !== before.map_url) {
    const contact: Record<string, string> = {}
    for (const [key, value] of Object.entries(site.contact ?? {})) {
      if (key !== 'map_url' && value) contact[key] = value
    }
    const map = draft.map_url.trim()
    if (map) contact.map_url = map
    body.contact = contact
  }

  return Object.keys(body).length ? body : null
}

/** The route: `/site/social`. The verbs are the site's (`site.can_view` to
 *  read, `site.can_update` to save), as they were in *Your firm*. */
export function SiteSocial() {
  const { can } = useEvaluation()
  const editable = can(SITE, 'can_update')
  const previewHref = useDraftPreviewHref()
  const [site, setSite] = useState<Site | null>(null)
  const [unavailable, setUnavailable] = useState(false)
  const [draft, setDraft] = useState<SocialDraft | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    siteApi
      .read()
      .then((body) => {
        setSite(body.data.site)
        setDraft(draftFrom(body.data.site))
      })
      .catch(() => setUnavailable(true))
  }, [])

  const patch = useMemo(() => (site && draft ? socialPatch(site, draft) : null), [site, draft])
  const dirty = patch !== null
  const guard = useLeaveGuard(dirty)

  const problems = draft
    ? [
        ...SOCIAL_NETWORKS.map(({ key, label }) => {
          const p = httpUrlProblem(draft.social[key] ?? '')
          return p ? `${label}: ${p}` : null
        }),
        (() => {
          const p = httpUrlProblem(draft.map_url)
          return p ? `Map link: ${p}` : null
        })(),
      ].filter((p): p is string => p !== null)
    : []
  const problem = problems[0] ?? null

  const save = async () => {
    if (!patch || problem) return
    setSaving(true)
    setError(null)
    try {
      const body = await siteApi.update(patch)
      setSite(body.data)
      setDraft(draftFrom(body.data))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Those links could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  const disabled = !editable || !draft || unavailable
  const others = site
    ? Object.keys(site.social ?? {}).filter((key) => !SOCIAL_NETWORKS.some((n) => n.key === key))
    : []
  const filled = draft
    ? SOCIAL_NETWORKS.filter(({ key }) => (draft.social[key] ?? '').trim()).length
    : 0
  const mapProblem = draft ? httpUrlProblem(draft.map_url) : null

  return (
    <EditPage
      title="Social profiles & map"
      back={{ to: '/site', label: 'Your website' }}
      description="Links to your firm’s profiles and a map to your office. Your website’s footer draws them when Social links or Map link is switched on in Navigation."
    >
      {guard}
      {error && <Alert tone="error">{error}</Alert>}

      {unavailable ? (
        <SiteSection id="social" icon={<Share2 size={18} aria-hidden />} title="Social profiles">
          <p className="text-muted-foreground text-sm">
            These live on your website, and it could not be read. They become available here once
            your website exists and you can open it.
          </p>
        </SiteSection>
      ) : !draft ? (
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-64 w-full rounded-2xl" />
          <Skeleton className="h-32 w-full rounded-2xl" />
        </div>
      ) : (
        <>
          <SiteSection
            id="social"
            icon={<Share2 size={18} aria-hidden />}
            title="Social profiles"
            description="Paste the full address of each profile. Leave one empty and it is left out."
            aside={
              <Badge size="sm" tone={filled ? 'success' : 'neutral'}>
                {filled} of {SOCIAL_NETWORKS.length}
              </Badge>
            }
          >
            <div className="grid gap-4 sm:grid-cols-2">
              {SOCIAL_NETWORKS.map(({ key, label, placeholder }) => (
                <Input
                  key={key}
                  label={label}
                  type="url"
                  inputMode="url"
                  spellCheck={false}
                  placeholder={placeholder}
                  value={draft.social[key] ?? ''}
                  disabled={disabled}
                  error={httpUrlProblem(draft.social[key] ?? '')}
                  onChange={(e) =>
                    setDraft((prev) =>
                      prev ? { ...prev, social: { ...prev.social, [key]: e.target.value } } : prev,
                    )
                  }
                />
              ))}
            </div>
            {others.length > 0 && (
              <p className="text-muted-foreground text-xs">
                Also on your site, and kept as they are: {others.join(', ')}.
              </p>
            )}
          </SiteSection>

          <SiteSection
            id="map"
            icon={<MapPin size={18} aria-hidden />}
            title="Map to your office"
            description="A share link from Google Maps, or any map, so a visitor can find you."
          >
            <div className="space-y-2">
              <Input
                label="Map link"
                type="url"
                inputMode="url"
                spellCheck={false}
                placeholder="https://maps.app.goo.gl/…"
                value={draft.map_url}
                disabled={disabled}
                error={mapProblem}
                onChange={(e) =>
                  setDraft((prev) => (prev ? { ...prev, map_url: e.target.value } : prev))
                }
              />
              {draft.map_url.trim() && !mapProblem ? (
                // To check the link goes where the firm means before a visitor
                // finds out it does not.
                <a
                  href={draft.map_url.trim()}
                  target="_blank"
                  rel="noreferrer"
                  data-map-open=""
                  className="text-primary inline-flex items-center gap-1 text-sm font-medium hover:underline"
                >
                  Open the map <ExternalLink size={14} aria-hidden />
                </a>
              ) : null}
            </div>
          </SiteSection>
        </>
      )}

      <EditBar
        dirty={dirty}
        saving={saving}
        problem={unavailable ? 'Your website could not be read, so there is nothing to save.' : problem}
        editable={editable}
        onSave={() => void save()}
        onDiscard={() => site && setDraft(draftFrom(site))}
        previewHref={previewHref}
      />
    </EditPage>
  )
}
